var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from "react";
import { EXPERIENCE, InsuranceProducts } from "../../constants";
import { PivotReason, hasBundledCurrentPolicy, selectInsuranceProduct, setBundlePivotReason, shouldUpsellBundle, } from "../../features/applicant/applicantSlice";
import { enteredUnlinkedApplication, saveHomeowners, savePolicyHolder, } from "../../features/unlinked-application/unlinkedApplicationSlice";
import { getAutoPolicies, getHomePolicies, getRentersPolicies, hasPolicies, isValidHomePolicy, } from "../../services/connect/utils";
import BasePoliciesLoadingRoute from "../../features/policies/routes/policies-loading";
import { PHASE } from "../../routeConfig";
import { TrellisPolicyTypes } from "@savvyinsure/savvy-auto-policy/lib/enums";
import getMiddleOfBirthdayRange from "../../utils/getMiddleOfBirthdayRange";
import useAccountReferenceId from "../../services/savvy/useAccountReferenceId";
import useAffiliateConfig from "../../hooks/useAffiliateConfig";
import useAppDispatch from "../../hooks/useAppDispatch";
import useAppSelector from "../../hooks/useAppSelector";
import useFindIssuer from "../../features/configuration/useFindIssuer";
import useMemoryHistory from "../../hooks/useMemoryHistory";
export default function AutoPoliciesLoadingRoute() {
    var _a = useAppSelector(function (state) { return state.auth.auto.accountInfo; }), connectionId = _a.connectionId, temporaryAccessKey = _a.temporaryAccessKey;
    var _b = useAppSelector(function (state) { return state.configuration; }), experience = _b.experience, products = _b.products;
    var insuranceProduct = useAppSelector(function (state) { return state.applicant.insuranceProduct; });
    var push = useMemoryHistory().push;
    var dispatch = useAppDispatch();
    var getAccountReferenceId = useAccountReferenceId().getAccountReferenceId;
    var findIssuer = useFindIssuer();
    var affiliateConfig = useAffiliateConfig();
    var noPolicyRoute = PHASE.VERIFY_CONTACT;
    var nextRoute = PHASE.POLICIES_LOADED;
    var onPoliciesLoaded = React.useCallback(function (policyResponse) {
        var _a, _b, _c, _d;
        var hasAutoPolicies = hasPolicies(policyResponse, TrellisPolicyTypes.PERSONAL_AUTO);
        var hasHomePolicies = hasPolicies(policyResponse, TrellisPolicyTypes.HOMEOWNERS);
        // @todo look into adding logic to parse multiple policies and extract an active one from the response
        var autoPolicy = getAutoPolicies(policyResponse.policies)[0];
        var homePolicy = getHomePolicies(policyResponse.policies)[0];
        var rentersPolicy = getRentersPolicies(policyResponse.policies)[0];
        var hasValidAutoPolicy = typeof autoPolicy !== "undefined" &&
            autoPolicy.vehicles.every(function (vehicle) { return Boolean(vehicle.vin); });
        var hasValidHomePolicy = isValidHomePolicy(homePolicy);
        var isBundleUser = insuranceProduct === InsuranceProducts.AutoHome;
        var isMonolineUser = !isBundleUser;
        if (!hasAutoPolicies || !hasValidAutoPolicy) {
            if (isBundleUser || affiliateConfig.quotesOnly) {
                dispatch(enteredUnlinkedApplication("missing-data"));
                return getAccountReferenceId({ accountConnectionId: null })
                    .unwrap()
                    .then(function () {
                    push(PHASE.AUTO_POLICIES_MISSING_DATA);
                });
            }
        }
        if (hasHomePolicies && hasValidHomePolicy) {
            var homeIssuer = (homePolicy === null || homePolicy === void 0 ? void 0 : homePolicy.issuer)
                ? findIssuer(homePolicy === null || homePolicy === void 0 ? void 0 : homePolicy.issuer)
                : undefined;
            dispatch(hasBundledCurrentPolicy(homeIssuer));
            // Auto Upsell to bundles for Savvy App users
            if (isMonolineUser &&
                experience === EXPERIENCE.SAVVY &&
                products.length > 1) {
                dispatch(shouldUpsellBundle());
            }
        }
        else if (isBundleUser) {
            /**
             * If the user has an auto policy and a renters policy on the account,
             * pivot the user to an auto only experience. If the user wants to insure a home
             * that is insured on a different insurance account, the agent will handle that.
             */
            if (rentersPolicy) {
                dispatch(selectInsuranceProduct({
                    product: InsuranceProducts.Auto,
                    changeRoute: false,
                }));
                dispatch(setBundlePivotReason(PivotReason.Rent));
                return push(nextRoute);
            }
            /**
             * If the user is a bundle search, but is missing linked homeowner data, send them to the unlinked application to finish
             */
            dispatch(enteredUnlinkedApplication("missing-data"));
            if (autoPolicy) {
                var primaryOperator = autoPolicy.operators.find(function (op) { return op.isPrimary; });
                dispatch(savePolicyHolder({
                    firstName: (_a = autoPolicy.policyHolder.name) === null || _a === void 0 ? void 0 : _a.firstName,
                    lastName: (_b = autoPolicy.policyHolder.name) === null || _b === void 0 ? void 0 : _b.lastName,
                    email: (_c = autoPolicy.policyHolder.email) !== null && _c !== void 0 ? _c : "",
                    phone: (_d = autoPolicy.policyHolder.phoneNumber) !== null && _d !== void 0 ? _d : "",
                    dateOfBirth: getMiddleOfBirthdayRange(primaryOperator === null || primaryOperator === void 0 ? void 0 : primaryOperator.birthdayRange, primaryOperator === null || primaryOperator === void 0 ? void 0 : primaryOperator.birthday),
                }));
                if (autoPolicy.policyHolder.address) {
                    var address = autoPolicy.policyHolder.address;
                    dispatch(saveHomeowners(__assign(__assign({ address1: "".concat(address.number).concat(address.prefix ? " ".concat(address.prefix) : "", " ").concat(address.street) }, (address.sec_unit_type && {
                        address2: "".concat(address.sec_unit_type, " ").concat(address.sec_unit_num),
                    })), { city: address.city, state: address.state, zip: address.zip })));
                }
            }
            return push(PHASE.UNLINKED_APPLICATION_HOMEOWNERS_INTRO);
        }
        push(hasAutoPolicies ? nextRoute : noPolicyRoute);
    }, [
        push,
        insuranceProduct,
        noPolicyRoute,
        dispatch,
        getAccountReferenceId,
        nextRoute,
        findIssuer,
        affiliateConfig.quotesOnly,
        experience,
        products.length,
    ]);
    return (React.createElement(BasePoliciesLoadingRoute, { connectionId: connectionId, temporaryAccessKey: temporaryAccessKey, onPoliciesLoaded: onPoliciesLoaded }));
}
